import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  Divider,
  Chip,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Rule as RuleIcon,
  Settings as SettingsIcon,
  Cloud as CloudIcon,
  Search as SearchIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Api as ApiIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  Build as BuildIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  TextFields as TextFieldsIcon,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PermissionWrapper } from '../common/PermissionWrapper';
import { TagOpsLogo } from '../common/TagOpsLogo';
import { UserRole } from '../../types/permissions';
import { usePermissions } from '../../hooks/usePermissions';
import { tagopsService } from '../../services/tagopsService';
import {
  isAwsMarketplaceSubscription,
  isFreeTrialSubscription,
} from '../../utils/subscriptionUtils';

interface AppSidebarProps {
  open: boolean;
  onClose: () => void;
  drawerWidth: number;
}

const menuItems = [
  {
    text: 'Overview',
    icon: <DashboardIcon />,
    path: '/',
    description: 'Overview and analytics',
    permission: 'ReadOnly' as UserRole,
  },
  {
    text: 'Tagging Rules',
    icon: <RuleIcon />,
    path: '/rules',
    description: 'Configure automatic tagging',
    permission: 'ReadOnly' as UserRole,
  },
  {
    text: 'Naming Conventions',
    icon: <TextFieldsIcon />,
    path: '/naming-conventions',
    description: 'Manage resource naming convention rules',
    permission: 'ReadOnly' as UserRole,
  },
  {
    text: 'Inventory',
    icon: <SearchIcon />,
    path: '/inventory',
    description: 'Resource inventory management',
    permission: 'ReadOnly' as UserRole,
  },
  {
    text: 'AWS Accounts',
    icon: <CloudIcon />,
    path: '/aws-accounts',
    description: 'Manage AWS account integrations',
    permission: 'ReadOnly' as UserRole,
  },
];

// Settings submenu items - visible to all users, but actions are restricted by role in the Settings component
const getSettingsItems = () => {
  const items = [
    {
      text: 'User Management',
      icon: <PeopleIcon />,
      path: '/settings?tab=user-management',
      description: 'Manage users and permissions',
      permission: 'ReadOnly' as UserRole,
    },
    {
      text: 'System Settings',
      icon: <BuildIcon />,
      path: '/settings?tab=system-settings',
      description: 'Configure system-wide settings',
      permission: 'ReadOnly' as UserRole,
    },
    {
      text: 'Account Settings',
      icon: <AccountBalanceIcon />,
      path: '/settings?tab=account-settings',
      description: 'Manage AWS account configurations',
      permission: 'ReadOnly' as UserRole,
    },
    {
      text: 'Subscription',
      icon: <CreditCardIcon />,
      path: '/settings?tab=subscription',
      description: 'Manage your subscription and billing',
      permission: 'ReadOnly' as UserRole,
    },
  ];

  items.push({
    text: 'API Tokens',
    icon: <ApiIcon />,
    path: '/settings?tab=api-tokens',
    description: 'Manage API tokens for programmatic access',
    permission: 'ReadOnly' as UserRole,
  });

  return items;
};




export const AppSidebar: React.FC<AppSidebarProps> = ({ open, onClose, drawerWidth }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdministrator } = usePermissions();
  const [settingsExpanded, setSettingsExpanded] = React.useState(false);
  const [currentPlan, setCurrentPlan] = React.useState<string>('Free');
  const [trialDaysLeft, setTrialDaysLeft] = React.useState<number | null>(null);

  const applySubscriptionStatus = React.useCallback((
    subscription: Awaited<ReturnType<typeof tagopsService.getSubscriptionStatus>>['subscription'],
    trialEndDate?: string | null,
  ) => {
    const daysUntil = (isoDate: string) => {
      const diffDays = Math.ceil((new Date(isoDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    };

    if (!subscription) {
      setCurrentPlan('Free Trial');
      setTrialDaysLeft(trialEndDate ? daysUntil(trialEndDate) : null);
      return;
    }

    if (isAwsMarketplaceSubscription(subscription)) {
      if (isFreeTrialSubscription(subscription)) {
        setCurrentPlan('Free Trial');
        setTrialDaysLeft(trialEndDate ? daysUntil(trialEndDate) : null);
      } else {
        setCurrentPlan('');
        setTrialDaysLeft(null);
      }
      return;
    }

    if (subscription.plan_id === 'fixed' || subscription.tier_type === 'Fixed') {
      setCurrentPlan('Fixed');
      setTrialDaysLeft(null);
      return;
    }

    if (subscription.status === 'ACTIVE' && subscription.plan_id !== 'free') {
      setCurrentPlan('');
      setTrialDaysLeft(null);
      return;
    }

    setCurrentPlan('Free Trial');
    setTrialDaysLeft(trialEndDate ? daysUntil(trialEndDate) : null);
  }, []);

  const handleSettingsExpandClick = () => {
    setSettingsExpanded(!settingsExpanded);
  };

  const handletagopsClick = () => {
    navigate('/');
    onClose(); // Close mobile drawer if open
  };

  // Auto-expand settings section when on settings page
  React.useEffect(() => {
    if (location.pathname === '/settings') {
      setSettingsExpanded(true);
    }
  }, [location.pathname]);

  // Load current subscription plan (only for Administrators)
  React.useEffect(() => {
    const loadSubscriptionPlan = async () => {
      if (!isAdministrator) {
        setCurrentPlan('Free Trial');
        return;
      }

      try {
        const response = await tagopsService.getSubscriptionStatus();
        applySubscriptionStatus(response.subscription, response.trial_end_date);
      } catch (error) {
        console.error('Error loading subscription plan:', error);
        setCurrentPlan('Free Trial');
        setTrialDaysLeft(null);
      }
    };

    loadSubscriptionPlan();
  }, [isAdministrator, applySubscriptionStatus]);

  // Refresh plan when user navigates to subscription page
  React.useEffect(() => {
    if (location.pathname === '/settings' && location.search.includes('tab=subscription') && isAdministrator) {
      const refreshPlan = async () => {
        try {
          const response = await tagopsService.getSubscriptionStatus();
          applySubscriptionStatus(response.subscription, response.trial_end_date);
        } catch (error) {
          console.error('Error refreshing subscription plan:', error);
        }
      };

      const timeoutId = setTimeout(refreshPlan, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname, location.search, isAdministrator, applySubscriptionStatus]);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Brand Header */}
      <Box
        sx={{
          pt: 0,
          px: 3,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
          color: 'white',
          cursor: 'pointer',
          '&:hover': {
            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
            transition: 'background 0.2s ease-in-out',
          },
        }}
        onClick={handletagopsClick}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: -8, mt: -6 }}>
          <TagOpsLogo size={200} variant="sidebar" />
        </Box>
        <Typography
          variant="caption"
          sx={{
            opacity: 0.9,
            fontWeight: 500,
            letterSpacing: '0.02em',
          }}
        >
          AWS Resource Tagging Management
        </Typography>
        {currentPlan && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={currentPlan}
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontSize: '0.7rem',
                fontWeight: 600,
              }}
            />
            {trialDaysLeft !== null && (
              <Chip
                label={`${trialDaysLeft} ${trialDaysLeft === 1 ? 'day' : 'days'} left`}
                size="small"
                sx={{
                  bgcolor: trialDaysLeft <= 3 ? 'rgba(255, 152, 0, 0.9)' : 'rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ pt: 1 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <PermissionWrapper key={item.text} permission={item.permission}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <Tooltip
                    title={item.description}
                    placement="right"
                    arrow
                    sx={{ display: { sm: 'none' } }}
                  >
                    <ListItemButton
                      component={Link}
                      to={item.path}
                      selected={isActive}
                      onClick={onClose}
                      sx={{
                        mx: 1,
                        borderRadius: 2,
                        minHeight: 48,
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          '& .MuiListItemIcon-root': {
                            color: 'white',
                          },
                        },
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: isActive ? 'white' : 'text.secondary',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontWeight: isActive ? 600 : 500,
                          fontSize: '0.9rem',
                        }}
                      />
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              </PermissionWrapper>
            );
          })}
        </List>

        <Divider sx={{ my: 2, mx: 2 }} />

        {/* Settings Section - Always visible to all users */}
        <List>
          <ListItem disablePadding>
            <Tooltip
              title="Configure system settings and user management"
              placement="right"
              arrow
              sx={{ display: { sm: 'none' } }}
            >
              <ListItemButton
                onClick={handleSettingsExpandClick}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  minHeight: 48,
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Settings"
                  primaryTypographyProps={{
                    fontWeight: 500,
                    fontSize: '0.9rem',
                  }}
                />
                {settingsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          <Collapse in={settingsExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {getSettingsItems().map((item) => {
                const isActive = location.pathname + location.search === item.path;
                return (
                  <PermissionWrapper key={item.text} permission={item.permission}>
                    <ListItem disablePadding>
                      <Tooltip
                        title={item.description}
                        placement="right"
                        arrow
                        sx={{ display: { sm: 'none' } }}
                      >
                        <ListItemButton
                          component={Link}
                          to={item.path}
                          selected={isActive}
                          onClick={onClose}
                          sx={{
                            pl: 6,
                            mx: 1,
                            borderRadius: 2,
                            minHeight: 40,
                            '&.Mui-selected': {
                              bgcolor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'white',
                              },
                            },
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 32,
                              color: isActive ? 'white' : 'text.secondary',
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontWeight: isActive ? 600 : 400,
                              fontSize: '0.85rem',
                            }}
                          />
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>
                  </PermissionWrapper>
                );
              })}
            </List>
          </Collapse>
        </List>

        <Divider sx={{ my: 2, mx: 2 }} />

      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.subtle',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            textAlign: 'center',
            fontWeight: 500,
          }}
        >
          TagOps
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            display: 'block',
            textAlign: 'center',
            mt: 0.5,
          }}
        >
          © 2026 TagOps Inc.
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRight: 'none',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRight: 'none',
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
}; 